import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { IAzurePrices } from '../../../services/azure/azurePrices/azurePrices.model';
import { RootState } from '../../app.model';
import {
  searchAzurePrices,
  getAzurePricesById,
  saveAzurePrices,
  deleteAzurePrices,
} from './azurePrices.action';
import { IAzurePricesState } from './azurePrices.model';

export const initialState: IAzurePricesState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const azurePricesSlice = createSlice({
  name: 'azurePrices',
  initialState,
  reducers: {
    clearAzurePrices: () => {
      return initialState;
    },
    clearAzurePricesMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearAzurePricesGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchAzurePrices.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchAzurePrices.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchAzurePrices.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getAzurePricesById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getAzurePricesById.fulfilled.type]: (state, action: PayloadAction<IAzurePrices>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getAzurePricesById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveAzurePrices.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveAzurePrices.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveAzurePrices.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteAzurePrices.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteAzurePrices.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteAzurePrices.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const azurePricesSelector = (state: RootState) => state.azurePrices;

// Actions
export const {
  clearAzurePrices,
  clearAzurePricesMessages,
  clearAzurePricesGetById,
  setTableColumnSelection,
} = azurePricesSlice.actions;

// The reducer
export default azurePricesSlice.reducer;
